// src/pages/work-order-management-system/components/PdfLibraryModal.jsx
import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/appIcon';

import { useResponsiveModalLayout } from './hooks/useResponsiveModalLayout';
import { usePdfLibrary } from './hooks/usePdfLibrary';
import { generarOrdenTrabajoPDF } from './pdfGeneratorOrder';
import generateProfessionalPDF from './pdfGenerator';

// Helpers de formato simples y profesionales
const formatDateTime = (value) => {
    if (!value) return 'Sin fecha';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return 'Sin fecha';

    try {
        return d.toLocaleString('es-MX', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return 'Sin fecha';
    }
};

const formatCurrency = (value) => {
    if (typeof value !== 'number') return '$0.00';
    try {
        return value.toLocaleString('es-MX', {
            style: 'currency',
            currency: 'MXN',
            minimumFractionDigits: 2
        });
    } catch {
        return `$${value.toFixed(2)}`;
    }
};

const getTypeLabel = (type) => {
    if (!type) return 'Sin tipo';
    if (type === 'work-order') return 'Orden de trabajo';
    if (type === 'service-report') return 'Reporte de trabajo';
    if (type === 'budget') return 'Presupuesto';
    return type;
};

const getSafeClient = (client) => {
    if (!client || !client.trim()) return 'Sin cliente';
    return client.trim();
};

const PdfLibraryModal = ({ isOpen, onClose, onEdit }) => {
    const { isMobile, windowHeight } = useResponsiveModalLayout();

    const {
        groupedByMonth,
        totalCount,
        totalFiltered,
        totalWorkOrders,
        totalServiceReports,
        totalBudgets,
        searchText,
        setSearchText,
        clientFilter,
        setClientFilter,
        clientOptions,
        typeFilter,
        setTypeFilter,
        typeOptions,
        monthFilter,
        setMonthFilter,
        monthOptions,
        handleDeletePdf,
        handleClearAll
    } = usePdfLibrary();

    // Cerrar con ESC
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    const handleResetFilters = () => {
        setSearchText('');
        setClientFilter('all');
        setTypeFilter('all');
        setMonthFilter('all');
    };

    const hasActiveFilters =
        searchText.trim() !== '' ||
        clientFilter !== 'all' ||
        typeFilter !== 'all' ||
        monthFilter !== 'all';

    // Handler universal: Ver (abrir guardado o regenerar desde meta.rawData)
    const handleView = async (pdf) => {
        if (pdf?.blobOrUrl) {
            try {
                window.open(pdf.blobOrUrl, '_blank', 'noreferrer');
            } catch {
                window.location.href = pdf.blobOrUrl;
            }
            return;
        }

        const raw = pdf?.meta?.rawData;
        if (!raw) {
            alert('Este documento no tiene datos guardados para regenerar.');
            return;
        }

        try {
            if (pdf.type === 'work-order') {
                generarOrdenTrabajoPDF(
                    raw.datosGenerales,
                    raw.refacciones,
                    raw.servicios,
                    raw.calculosOrden,
                    raw.incluirIVA,
                    raw.incluirISR
                );
                return;
            }

            if (pdf.type === 'service-report') {
                await generateProfessionalPDF(raw);
                return;
            }

            alert('Tipo de documento no soportado.');
        } catch (error) {
            console.error('[PdfLibrary] Error al regenerar/abrir PDF:', error);
            alert('No se pudo abrir este documento.');
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 overflow-hidden">
                    {/* Overlay */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/75 backdrop-blur-md"
                        onClick={onClose}
                    />

                    {/* Contenedor principal (full screen, diseño sobrio) */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ type: 'spring', damping: 22, stiffness: 260 }}
                        className="relative z-10 w-full h-full bg-slate-950 border border-slate-900 shadow-[0_0_0_1px_rgba(15,23,42,0.9),0_32px_96px_rgba(0,0,0,0.9)] flex flex-col"
                        style={isMobile ? { height: `${windowHeight}px` } : { height: '100vh' }}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Historial de PDFs"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* HEADER */}
                        <header className="flex items-start justify-between gap-4 px-4 lg:px-6 py-4 border-b border-slate-800 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 pt-12 lg:pt-4">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                <div className="hidden sm:flex w-11 h-11 rounded-xl bg-slate-900 border border-slate-800 items-center justify-center flex-shrink-0">
                                    <Icon name="FolderOpen" className="w-5 h-5 text-sky-400" />
                                </div>
                                <div className="flex-1 min-w-0 space-y-1">
                                    <div className="flex items-center gap-2">
                                        <h1 className="text-lg sm:text-xl font-semibold text-white truncate">
                                            Historial de PDFs
                                        </h1>
                                        <span className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-2 py-0.5 text-[11px] font-mono text-slate-200">
                                            {totalFiltered}/{totalCount}
                                        </span>
                                    </div>
                                    <p className="text-xs sm:text-sm text-slate-300">
                                        Visor centralizado de órdenes de trabajo y presupuestos generados en el sistema.
                                    </p>
                                    <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px] text-slate-300">
                                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 border border-slate-800 px-2 py-0.5">
                                            <Icon name="FileText" className="w-3 h-3 text-sky-400" />
                                            Órdenes: {totalWorkOrders}
                                        </span>
                                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 border border-slate-800 px-2 py-0.5">
                                            <Icon name="FileText" className="w-3 h-3 text-violet-400" />
                                            Reportes: {totalServiceReports}
                                        </span>
                                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 border border-slate-800 px-2 py-0.5">
                                            <Icon name="FileText" className="w-3 h-3 text-emerald-400" />
                                            Presupuestos: {totalBudgets}
                                        </span>
                                        {hasActiveFilters && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 border border-sky-500/50 px-2 py-0.5 text-sky-200">
                                                <Icon name="Filter" className="w-3 h-3" />
                                                Filtros activos
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={onClose}
                                className="p-2 lg:p-3 rounded-xl hover:bg-slate-900 text-slate-400 hover:text-white transition-colors"
                                aria-label="Cerrar historial de PDFs"
                                type="button"
                            >
                                <Icon name="X" className="w-5 h-5" />
                            </button>
                        </header>

                        {/* FILTROS */}
                        <section className="border-b border-slate-800 bg-slate-950/95 px-4 lg:px-6 py-3 space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                {/* Búsqueda */}
                                <div className="md:col-span-2">
                                    <Input
                                        label="Buscar (cliente, folio, nombre, tipo...)"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        className="bg-slate-950/70 border-slate-800 text-slate-100 text-sm"
                                        placeholder="Ej. Juan Pérez, OT-123, Presupuesto..."
                                    />
                                </div>

                                {/* Cliente */}
                                <div className="flex flex-col gap-1 text-xs text-slate-400">
                                    <span>Cliente</span>
                                    <select
                                        value={clientFilter}
                                        onChange={(e) => setClientFilter(e.target.value)}
                                        className="w-full bg-slate-950/70 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-500/70"
                                    >
                                        <option value="all">Todos</option>
                                        {clientOptions.map((c) => (
                                            <option key={c} value={c}>
                                                {c}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Tipo */}
                                <div className="flex flex-col gap-1 text-xs text-slate-400">
                                    <span>Tipo de documento</span>
                                    <select
                                        value={typeFilter}
                                        onChange={(e) => setTypeFilter(e.target.value)}
                                        className="w-full bg-slate-950/70 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-500/70"
                                    >
                                        <option value="all">Todos</option>
                                        {typeOptions.map((t) => (
                                            <option key={t} value={t}>
                                                {getTypeLabel(t)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Mes + acciones */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
                                    <span className="inline-flex items-center gap-1">
                                        <Icon name="Calendar" className="w-3 h-3 text-sky-400" />
                                        Mes:
                                    </span>
                                    <select
                                        value={monthFilter}
                                        onChange={(e) => setMonthFilter(e.target.value)}
                                        className="bg-slate-950/70 border border-slate-800 rounded-xl px-2 py-1.5 text-[11px] text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-500/70"
                                    >
                                        <option value="all">Todos los meses</option>
                                        {monthOptions.map((m) => (
                                            <option key={m.value} value={m.value}>
                                                {m.label}
                                            </option>
                                        ))}
                                    </select>
                                    <span className="hidden sm:inline text-slate-500">
                                        • Mostrando {totalFiltered} de {totalCount}
                                    </span>
                                </div>

                                <div className="flex flex-wrap items-center gap-2 justify-end">
                                    {hasActiveFilters && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={handleResetFilters}
                                            className="border-slate-700 text-slate-200 hover:bg-slate-900 hover:text-white px-3 py-2 text-[11px] min-h-[34px]"
                                        >
                                            <Icon name="X" className="w-3 h-3 mr-1" />
                                            Limpiar filtros
                                        </Button>
                                    )}
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleClearAll}
                                        className="border-rose-500/60 text-rose-200 hover:bg-rose-500/10 hover:text-rose-100 px-3 py-2 text-[11px] min-h-[34px]"
                                    >
                                        <Icon name="Trash2" className="w-3 h-3 mr-1" />
                                        Limpiar historial
                                    </Button>
                                </div>
                            </div>

                            {/* Chips de filtros activos (sobrio) */}
                            {hasActiveFilters && (
                                <div className="flex flex-wrap gap-2 text-[10px] text-slate-300 mt-1">
                                    {searchText.trim() !== '' && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 border border-slate-800 px-2 py-0.5">
                                            <Icon name="Search" className="w-3 h-3" />“{searchText.trim()}”
                                        </span>
                                    )}
                                    {clientFilter !== 'all' && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 border border-slate-800 px-2 py-0.5">
                                            <Icon name="User" className="w-3 h-3" />
                                            Cliente: {clientFilter}
                                        </span>
                                    )}
                                    {typeFilter !== 'all' && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 border border-slate-800 px-2 py-0.5">
                                            <Icon name="FileText" className="w-3 h-3" />
                                            Tipo: {getTypeLabel(typeFilter)}
                                        </span>
                                    )}
                                    {monthFilter !== 'all' && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 border border-slate-800 px-2 py-0.5">
                                            <Icon name="Calendar" className="w-3 h-3" />
                                            Mes: {monthOptions.find((m) => m.value === monthFilter)?.label}
                                        </span>
                                    )}
                                </div>
                            )}
                        </section>

                        {/* CONTENIDO PRINCIPAL */}
                        <main className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4 lg:p-6 bg-slate-950">
                            {groupedByMonth.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 gap-3">
                                    <div className="w-14 h-14 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center">
                                        <Icon name="FileSearch" className="w-7 h-7 text-slate-500" />
                                    </div>
                                    <p className="text-sm font-medium text-slate-100">
                                        No hay documentos para los filtros actuales
                                    </p>
                                    <p className="text-xs text-slate-500 max-w-xs">
                                        Genera un PDF desde tus módulos de orden de trabajo o presupuesto para que aparezcan aquí.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {groupedByMonth.map((group) => (
                                        <section key={group.key} className="space-y-2">
                                            {/* Encabezado de mes */}
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center">
                                                        <Icon name="Calendar" className="w-4 h-4 text-sky-400" />
                                                    </div>
                                                    <div>
                                                        <h2 className="text-sm font-semibold text-slate-100">{group.label}</h2>
                                                        <p className="text-[11px] text-slate-500">
                                                            {group.items.length} documento{group.items.length === 1 ? '' : 's'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Lista de PDFs */}
                                            <div className="space-y-2">
                                                {group.items.map((pdf) => {
                                                    const client = getSafeClient(pdf.client);
                                                    const typeLabel = getTypeLabel(pdf.type);
                                                    const dateLabel = formatDateTime(pdf.createdAt);
                                                    const totalLabel = typeof pdf.total === 'number' ? formatCurrency(pdf.total) : '—';

                                                    return (
                                                        <div
                                                            key={pdf.id}
                                                            className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl bg-slate-950/80 border border-slate-800 px-3 py-3 sm:px-4 sm:py-3"
                                                        >
                                                            {/* Info principal */}
                                                            <div className="flex-1 min-w-0 flex items-start gap-3">
                                                                <div className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center flex-shrink-0">
                                                                    <Icon
                                                                        name={
                                                                            pdf.type === 'work-order'
                                                                                ? 'FileText'
                                                                                : pdf.type === 'budget'
                                                                                    ? 'FileText'
                                                                                    : 'File'
                                                                        }
                                                                        className="w-4 h-4 text-sky-400"
                                                                    />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2">
                                                                        <p className="text-sm font-medium text-slate-100 truncate">{pdf.name}</p>
                                                                        {pdf.type && (
                                                                            <span className="inline-flex items-center rounded-full border border-slate-700 px-2 py-0.5 text-[10px] text-slate-200">
                                                                                {typeLabel}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-400">
                                                                        <span className="inline-flex items-center gap-1">
                                                                            <Icon name="User" className="w-3 h-3 text-slate-500" />
                                                                            {client}
                                                                        </span>
                                                                        {pdf.folio && (
                                                                            <span className="inline-flex items-center gap-1">
                                                                                <Icon name="Hash" className="w-3 h-3 text-slate-500" />
                                                                                Folio: <span className="font-mono">{pdf.folio}</span>
                                                                            </span>
                                                                        )}
                                                                        <span className="inline-flex items-center gap-1">
                                                                            <Icon name="CircleDollarSign" className="w-3 h-3 text-emerald-400" />
                                                                            Total:{' '}
                                                                            <span className="font-mono text-emerald-300">{totalLabel}</span>
                                                                        </span>
                                                                    </div>
                                                                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-500">
                                                                        <span className="inline-flex items-center gap-1">
                                                                            <Icon name="Clock" className="w-3 h-3" />
                                                                            {dateLabel}
                                                                        </span>
                                                                        {pdf.source && (
                                                                            <span className="inline-flex items-center gap-1">
                                                                                <Icon name="AppWindow" className="w-3 h-3" />
                                                                                Origen: {pdf.source}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Acciones */}
                                                            <div className="flex items-center gap-2 justify-end">
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    onClick={() => handleView(pdf)}
                                                                    disabled={!pdf?.blobOrUrl && !pdf?.meta?.rawData}
                                                                    className="border-sky-500/70 text-sky-200 hover:bg-sky-500/10 hover:text-sky-50 px-3 py-1.5 text-[11px] min-h-[34px]"
                                                                >
                                                                    <Icon name="ExternalLink" className="w-3 h-3 mr-1" />
                                                                    Ver
                                                                </Button>

                                                                {/* ✅ EDITAR CORREGIDO */}
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();

                                                                        const rawData = pdf?.meta?.rawData;
                                                                        if (!rawData) return;

                                                                        onEdit?.({
                                                                            type: pdf.type,
                                                                            rawData
                                                                        });

                                                                        onClose?.(); // cerrar librería
                                                                    }}
                                                                    disabled={!pdf?.meta?.rawData}
                                                                    className="border-emerald-500/60 text-emerald-100 hover:bg-emerald-500/10 hover:text-emerald-50 px-3 py-1.5 text-[11px] min-h-[34px]"
                                                                >
                                                                    <Icon name="Pencil" className="w-3 h-3 mr-1" />
                                                                    Editar
                                                                </Button>

                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    onClick={() => handleDeletePdf(pdf.id)}
                                                                    className="border-rose-500/60 text-rose-200 hover:bg-rose-500/10 hover:text-rose-50 px-3 py-1.5 text-[11px] min-h-[34px]"
                                                                >
                                                                    <Icon name="Trash2" className="w-3 h-3 mr-1" />
                                                                    Eliminar
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </section>
                                    ))}
                                </div>
                            )}
                        </main>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default PdfLibraryModal;
